import { Button } from '@/components/ui/button';
import { EntityDetailsHeader, type EntityDetailsHeaderActions } from '@/components/ui/entity-details-header';
import { cn } from '@/lib/utils';
import { Trash2, X, Copy, ChevronRight } from 'lucide-react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

interface DetailsPanelProps<TData = unknown> {
	isOpen?: boolean; // Optional for backwards compatibility, always shows panel if data exists
	onClose?: () => void; // Optional for mobile back button
	data: TData | null;
	title?: string;
	onEdit?: (id: string) => void;
	onDelete?: (id: string) => void;
	onClone?: (id: string) => void;
	onOpenInNewWindow?: (id: string) => void;
	renderContent?: (data: TData) => React.ReactNode;
	children?: React.ReactNode;
	getRecordId?: (data: TData) => string;
	getDescription?: (data: TData) => React.ReactNode;
	getStatusBadge?: (data: TData) => React.ReactNode; // For displaying status badges in header
	renderHeaderAction?: (data: TData) => React.ReactNode; // Custom action button in header
	alwaysOpen?: boolean; // If true, panel is always visible even without data
	showCloseButton?: boolean; // For mobile responsive close
	// New props for enhanced header
	renderEntityIcon?: (data: TData) => React.ReactNode; // Icon/avatar for the entity
	getEntityTypeName?: (data: TData) => string; // e.g., "Business" or "Individual"
	getHeaderSubtitle?: (data: TData) => React.ReactNode; // e.g., email link shown between name and entity type
	getLastUpdated?: (data: TData) => string; // Formatted relative time like "Updated 2 days ago"
	renderHeader?: (data: TData, actions: EntityDetailsHeaderActions) => React.ReactNode;
}

const resolveRecordId = <TData,>(
	data: TData | null,
	getRecordId?: (data: TData) => string
) => {
	if (!data) return undefined;
	const rawId = getRecordId
		? getRecordId(data)
		: // eslint-disable-next-line @typescript-eslint/no-explicit-any
			(data as any)?.id;

	if (rawId == null) {
		return undefined;
	}

	return String(rawId);
};

export function DetailsPanel<TData = unknown>({
	isOpen = true, // Default to always open
	onClose,
	data,
	title = 'Details',
	onEdit,
	onDelete,
	onClone,
	onOpenInNewWindow,
	renderContent,
	children,
	getRecordId,
	getDescription,
	getStatusBadge,
	renderHeaderAction,
	alwaysOpen = true,
	showCloseButton = false,
	renderEntityIcon,
	getEntityTypeName,
	getHeaderSubtitle,
	getLastUpdated,
	renderHeader
}: DetailsPanelProps<TData>) {
	const { t } = useTranslation('common');
	// For backwards compatibility and mobile responsiveness
	const isActuallyOpen = alwaysOpen || isOpen;
	const [isMounted, setIsMounted] = React.useState(isActuallyOpen);

	React.useEffect(() => {
		if (isActuallyOpen) {
			setIsMounted(true);
			return;
		}

		const timeout = setTimeout(() => setIsMounted(false), 300);

		return () => {
			clearTimeout(timeout);
		};
	}, [isActuallyOpen]);

	React.useEffect(() => {
		if (!isActuallyOpen || !showCloseButton || !onClose) return;

		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key === 'Escape') {
				onClose();
			}
		};

		window.addEventListener('keydown', handleKeyDown);

		return () => {
			window.removeEventListener('keydown', handleKeyDown);
		};
	}, [isActuallyOpen, onClose, showCloseButton]);

	const recordId = resolveRecordId(data, getRecordId);
	const descriptionContent =
		data && getDescription ? getDescription(data) : undefined;
	const statusBadgeContent =
		data && getStatusBadge ? getStatusBadge(data) : undefined;
	const entityIconContent =
		data && renderEntityIcon ? renderEntityIcon(data) : undefined;
	const entityTypeNameContent =
		data && getEntityTypeName ? getEntityTypeName(data) : undefined;
	const headerSubtitleContent =
		data && getHeaderSubtitle ? getHeaderSubtitle(data) : undefined;
	const lastUpdatedContent =
		data && getLastUpdated ? getLastUpdated(data) : undefined;
	const shouldRenderContent = isActuallyOpen || isMounted;
	const headerActions: EntityDetailsHeaderActions = {
		entityId: recordId,
		onEdit,
		onClone,
		onDelete,
		onOpenInNewWindow,
		onClose,
		showCloseButton
	};
	const customHeader = data && renderHeader ? renderHeader(data, headerActions) : null;

	// Use enhanced header layout when entity icon is provided
	const useEnhancedHeader = Boolean(entityIconContent);

	const renderedContent = data ? (
		renderContent ? (
			renderContent(data)
		) : (
			children
		)
	) : (
		<div className="py-16 text-sm text-center text-gray-500 dark:text-muted-foreground">
			{t('labels.selectItemToView', 'Select an item to view details')}
		</div>
	);

	return (
		<aside
			className={cn(
				'details-panel group flex h-full flex-none flex-col overflow-hidden',
				alwaysOpen
					? 'w-full opacity-100' // Always full width, resizing handled by parent
					: isActuallyOpen
						? 'w-full opacity-100 sm:w-72 lg:w-80 transition-all duration-300 ease-in-out'
						: 'w-0 opacity-0 pointer-events-none transition-all duration-300 ease-in-out'
			)}
			aria-hidden={!isActuallyOpen ? 'true' : 'false'}
			data-state={isActuallyOpen ? 'open' : 'closed'}
		>
			{shouldRenderContent && (
				<div
					className={cn(
						'relative flex h-full w-full flex-col',
						!alwaysOpen &&
							'transition-[opacity,transform] duration-300 ease-in-out',
						!alwaysOpen &&
							(isActuallyOpen
								? 'translate-x-0 opacity-100'
								: 'translate-x-6 opacity-0')
					)}
				>
					{/* Fixed Header */}
					<div className="flex-none p-4 details-panel-header bg-white dark:bg-slate-900">
						{customHeader ? (
							customHeader
						) : useEnhancedHeader ? (
							/* Enhanced header with avatar/icon - using shared component */
							<EntityDetailsHeader
								icon={entityIconContent}
								name={descriptionContent ?? title}
								statusBadge={statusBadgeContent}
								entityTypeName={entityTypeNameContent}
								subtitle={headerSubtitleContent}
								lastUpdated={lastUpdatedContent}
								entityId={recordId}
								onEdit={onEdit}
								onClone={onClone}
								onDelete={onDelete}
								onOpenInNewWindow={onOpenInNewWindow}
								onClose={onClose}
								showCloseButton={showCloseButton}
								renderAction={data && renderHeaderAction ? () => renderHeaderAction(data) : undefined}
							/>
						) : (
							/* Legacy header layout */
							<div className="flex items-start justify-between gap-3">
								<div className="flex-1 space-y-1">
									<div className="flex items-center gap-2">
										<p className="text-base text-[rgb(108,117,125)] uppercase font-base dark:text-slate-300">
											{title}
										</p>
										{statusBadgeContent}
									</div>
									{descriptionContent && (
										<div className="font-semibold text-[rgb(108,117,125)] font-lg dark:text-white">
											{descriptionContent}
										</div>
									)}
								</div>
								<div className="flex items-center gap-2">
									{data && renderHeaderAction && renderHeaderAction(data)}
									{data && recordId && onEdit && (
										<Button
											size="sm"
											onClick={() => onEdit(recordId)}
											className="px-4 font-medium text-white bg-blue-500 border-0 rounded-md shadow-sm h-9 hover:bg-blue-600"
										>
											{t('buttons.edit')}
										</Button>
									)}
									{data && recordId && onClone && (
										<Button
											size="sm"
											variant="ghost"
											onClick={() => onClone(recordId)}
											className="p-0 h-9 w-9 text-[rgb(108,117,125)] hover:text-green-600 hover:bg-transparent dark:text-slate-400 dark:hover:text-green-400"
											title="Clone"
										>
											<Copy className="w-4 h-4" />
										</Button>
									)}
									{data && recordId && onDelete && (
										<Button
											size="sm"
											variant="destructive"
											onClick={() => onDelete(recordId)}
											className="p-0 text-white border-0 rounded-md shadow-sm h-9 w-9"
										>
											<Trash2 className="w-4 h-4" />
										</Button>
									)}
									{showCloseButton && onClose && (
										<Button
											variant="ghost"
											size="icon"
											onClick={onClose}
											className="text-gray-500 h-7 w-7 hover:text-gray-800 dark:text-slate-400 dark:hover:text-white sm:hidden"
										>
											<X className="w-4 h-4" />
										</Button>
									)}
								</div>
							</div>
						)}
					</div>

					{/* Content Area - entity details manage their own scrolling */}
					<div className="flex-1 p-4 overflow-hidden flex flex-col min-h-0">
						<div className="flex-1 flex flex-col min-h-0 text-base text-gray-800 dark:text-slate-200">
							{renderedContent}
						</div>
					</div>
				</div>
			)}
		</aside>
	);
}

interface FieldValueProps {
	children: React.ReactNode;
	className?: string;
}

export function FieldValue({ children, className }: FieldValueProps) {
	return (
		<div className={cn('flex-1 text-sm font-semibold text-gray-900 dark:text-white', className)}>
			{children}
		</div>
	);
}

/**
 * A code/monospace value display for technical fields like IP addresses, keys, etc.
 */
export function CodeValue({ children, className }: FieldValueProps) {
	return (
		<div className={cn(
			'inline-block px-3 py-1.5 mt-1 text-sm font-normal font-mono bg-gray-50 border border-gray-200 rounded-md text-gray-800',
			'dark:bg-slate-800 dark:border-slate-600 dark:text-slate-200',
			className
		)}>
			{children}
		</div>
	);
}

interface DetailFieldProps {
	label: string;
	value: React.ReactNode;
	className?: string;
}

export function DetailField({ label, value, className }: DetailFieldProps) {
	// Don't render the field at all if value is null/undefined/empty string
	if (value == null || value === '') {
		return null;
	}

	return (
		<div className={cn('items-baseline gap-2', className)}>
			<p className="flex-shrink-0 text-[11px] uppercase font-normal tracking-wide text-gray-500 dark:text-slate-400">
				{label}
			</p>
			<FieldValue>{value}</FieldValue>
		</div>
	);
}

interface DetailFieldsGridProps {
	children: React.ReactNode;
	columns?: number;
	className?: string;
}

export function DetailFieldsGrid({
	children,
	columns = 2,
	className
}: DetailFieldsGridProps) {
	return (
		<div
			className={cn(
				'grid gap-3 max-w-[600px]',
				columns === 1 && 'grid-cols-1',
				columns === 2 && 'grid-cols-2',
				columns === 3 && 'grid-cols-3',
				className
			)}
		>
			{children}
		</div>
	);
}

interface DetailSectionProps {
	children: React.ReactNode;
	title?: string;
	action?: React.ReactNode;
	className?: string;
	noBorderTop?: boolean;
	collapsible?: boolean;
	rowCount?: number;
	defaultCollapsed?: boolean;
	persistenceKey?: string;
	onToggle?: (isCollapsed: boolean) => void;
}

const detailSectionCollapseMemory = new Map<string, boolean>();

const resolveDetailSectionMemoryKey = (
	title?: React.ReactNode,
	persistenceKey?: string
) => {
	if (persistenceKey) {
		return persistenceKey;
	}

	if (typeof title !== 'string' && typeof title !== 'number') {
		return null;
	}

	const locationKey =
		typeof window !== 'undefined' ? window.location.pathname : 'global';
	const normalizedTitle = String(title).trim().toLowerCase();
	return `${locationKey}::${normalizedTitle}`;
};

export function DetailSection({
	children,
	title,
	action,
	className,
	noBorderTop = false,
	collapsible = false,
	rowCount,
	defaultCollapsed = false,
	persistenceKey,
	onToggle
}: DetailSectionProps) {
	const isCollapsible =
		Boolean(title) && (collapsible || typeof rowCount === 'number');
	const memoryKey = React.useMemo(
		() => resolveDetailSectionMemoryKey(title, persistenceKey),
		[title, persistenceKey]
	);

	const [isCollapsed, setIsCollapsed] = React.useState(() => {
		if (!isCollapsible) {
			return false;
		}

		if (memoryKey && detailSectionCollapseMemory.has(memoryKey)) {
			return Boolean(detailSectionCollapseMemory.get(memoryKey));
		}

		return defaultCollapsed;
	});

	React.useEffect(() => {
		if (!isCollapsible) {
			setIsCollapsed(false);
			return;
		}

		if (memoryKey && detailSectionCollapseMemory.has(memoryKey)) {
			setIsCollapsed(Boolean(detailSectionCollapseMemory.get(memoryKey)));
			return;
		}

		setIsCollapsed(defaultCollapsed);
	}, [isCollapsible, memoryKey, defaultCollapsed]);

	React.useEffect(() => {
		if (!isCollapsible || !memoryKey) {
			return;
		}

		detailSectionCollapseMemory.set(memoryKey, isCollapsed);
	}, [isCollapsible, memoryKey, isCollapsed]);

	const handleToggleCollapsed = React.useCallback(() => {
		setIsCollapsed(previous => {
			const next = !previous;
			if (memoryKey) {
				detailSectionCollapseMemory.set(memoryKey, next);
			}
			onToggle?.(next);
			return next;
		});
	}, [memoryKey, onToggle]);

	const content = (
		<div className="space-y-2.5 text-sm text-gray-700 dark:text-slate-300">
			{children}
		</div>
	);
	const sectionTitleClass =
		'text-sm font-normal tracking-wide text-gray-500 uppercase dark:text-slate-400';
	const rowCountDisplay =
		typeof rowCount === 'number' ? (
			<span className="text-sm font-semibold tracking-normal normal-case text-gray-600 dark:text-slate-300">
				({rowCount})
			</span>
		) : null;

	return (
		<section
			className={cn(
				'details-panel-section',
				className
			)}
		>
			{title && (
				<div
					className={cn(
						'mb-3 flex items-center gap-3',
						noBorderTop
							? 'mt-0 pt-0 justify-center'
							: 'mt-4 pt-3 justify-end'
					)}
				>
					{!noBorderTop && (
						<div
							aria-hidden="true"
							className="h-px flex-1 bg-gray-200 dark:bg-slate-600/50"
						/>
					)}
					<div
						className="flex shrink-0 items-center gap-2"
					>
						{isCollapsible ? (
							<button
								type="button"
								onClick={handleToggleCollapsed}
								className="group inline-flex min-w-0 items-center justify-center gap-1 text-center transition-colors duration-150 hover:[&_h3]:text-gray-700 dark:hover:[&_h3]:text-slate-200"
							>
								<span className="inline-flex items-baseline gap-1">
									<h3 className={sectionTitleClass}>
										{title}
									</h3>
									{rowCountDisplay}
								</span>
								<ChevronRight
									className={cn(
										'h-4 w-4 shrink-0 text-gray-400 transition-transform duration-200 group-hover:text-gray-600 dark:text-slate-500 dark:group-hover:text-slate-300',
										!isCollapsed && 'rotate-90'
									)}
								/>
							</button>
						) : (
							<h3 className={sectionTitleClass}>
								{title}
							</h3>
						)}
						{action}
					</div>

				</div>
			)}
			{isCollapsible ? (
				<div
					className={cn(
						'grid overflow-hidden transition-[grid-template-rows,opacity] duration-300 ease-out',
						isCollapsed
							? 'grid-rows-[0fr] opacity-0'
							: 'grid-rows-[1fr] opacity-100'
					)}
				>
					<div className="overflow-hidden">{content}</div>
				</div>
			) : (
				content
			)}
		</section>
	);
}
